import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { batch, useDispatch, useSelector } from 'react-redux';
import { flatten, uniq } from 'lodash';
import { Tappable } from '@vectord/components';
import { TypeUtils } from '@vectord/utils';
import cogoToast from 'cogo-toast';

import {
  hasContestLesson,
  isHybridContest,
  RESCHEDULE_CLASSES_PAGE_TRACKINGS,
  RESCHEDULE_CLASSES_STAGES,
  rescheduleClassesFormCommunicationsSelector,
  resetRescheduleClassesForm,
  resetRescheduleFilters,
  sbatGroupListSelector,
  setBulkRescheduleValidateErrorMessage,
  setRescheduleClassesFormErrors,
  setRescheduleClassesStage,
  useBulkUpdateSbatGroupsMutation,
  useFilteredRescheduleClasses,
  useValidateUpdateMutation,
  validateRevisedSchedule,
} from 'features/classCreation';
import analytics from 'utils/analytics';

function ConfirmRevisedScheduleButton() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { superBatchId } = useParams();
  const state = useSelector((state) => state.rescheduleClassesForm);
  const communications = useSelector(
    rescheduleClassesFormCommunicationsSelector
  );
  const selectedSbatGroups = useSelector(sbatGroupListSelector);

  const [bulkUpdateSbatGroups, { isSuccess, error, isLoading: isUpdating }] =
    useBulkUpdateSbatGroupsMutation();

  const [validateUpdate, { data: response, isLoading: isValidating }] =
    useValidateUpdateMutation();

  const filteredSbatGroups = useFilteredRescheduleClasses(
    Object.values(state.sbatGroups)
  );

  const rescheduleWithComms = useMemo(
    () => Object.values(communications).some((val) => val),
    [communications]
  );

  useEffect(() => {
    if (response?.success) {
      bulkUpdateSbatGroups(state);
    } else if (response?.success === false) {
      batch(() => {
        dispatch(
          setRescheduleClassesStage(RESCHEDULE_CLASSES_STAGES.validation)
        );
        dispatch(setBulkRescheduleValidateErrorMessage(response?.message));
      });
    }
  }, [bulkUpdateSbatGroups, dispatch, response, state]);

  const handleClick = useCallback(() => {
    if (isValidating) return;

    const superBatchNames = uniq(
      flatten(
        Object.values(state.sbatGroups).map((sbatGroup) =>
          sbatGroup.super_batch_academy_topics.map(
            (sbat) => sbat.super_batch.name
          )
        )
      )
    );
    const retainedClasses = Object.values(state.sbatGroups).filter(
      (sbatGroup) => !sbatGroup.locked
    );
    const filteredClasses = filteredSbatGroups.filter(
      (sbatGroup) => sbatGroup.locked
    );
    const lockedClasses = Object.values(state.sbatGroups).filter(
      (sbatGroup) => sbatGroup.locked
    );
    let hybridContestNames = [];
    let contestNames = [];
    const sbatGroups = state.sbatGroups;
    Object.values(sbatGroups).forEach((sbatGroup) => {
      const { academy_topic: academyTopic } = sbatGroup;
      if (isHybridContest(academyTopic.lessons)) {
        hybridContestNames.push(academyTopic.title);
      }
      if (hasContestLesson(academyTopic.lessons)) {
        contestNames.push(academyTopic.title);
      }
    });

    analytics.click({
      click_type: RESCHEDULE_CLASSES_PAGE_TRACKINGS.confirmReschedule,
      custom: {
        num_of_retained_classes: retainedClasses.length,
        num_of_filtered_classes: filteredClasses.length,
        num_of_locked_classes: lockedClasses.length,
        locked_class_ids: lockedClasses.map((o) => o.id),
        retained_class_ids: retainedClasses.map((o) => o.id),
        super_batch_names: superBatchNames,
        hybrid_contest_count: hybridContestNames.length,
        hybrid_contest_name: hybridContestNames,
        contest_count: contestNames.length,
        contest_names: contestNames,
      },
    });

    const errors = validateRevisedSchedule(state);
    if (Object.keys(errors).length > 0) {
      dispatch(setRescheduleClassesFormErrors(errors));
    } else {
      dispatch(setRescheduleClassesFormErrors({}));
      const sbatGroupIds = selectedSbatGroups.map((sb) => sb.id);
      validateUpdate({ sbatGroupIds });
    }
  }, [
    dispatch,
    filteredSbatGroups,
    isValidating,
    selectedSbatGroups,
    state,
    validateUpdate,
  ]);

  useEffect(() => {
    if (isSuccess) {
      cogoToast.success('Classes have been rescheduled successfully!');
      batch(() => {
        dispatch(resetRescheduleClassesForm());
        dispatch(resetRescheduleFilters());
        dispatch(setRescheduleClassesStage(RESCHEDULE_CLASSES_STAGES.view));
      });
      navigate(`/classes/view-schedule/${superBatchId}`);
    }
  }, [
    dispatch,
    filteredSbatGroups,
    isSuccess,
    navigate,
    state.academyTopics,
    state.sbatGroups,
    superBatchId,
  ]);

  useEffect(() => {
    if (error) {
      const message = error?.data?.message;

      if (message?.base && TypeUtils.isArray(message.base)) {
        dispatch(setRescheduleClassesFormErrors({ base: message.base }));
      } else {
        cogoToast.error(
          message ||
            'Failed to reschedule classes. Please try again after sometime!'
        );
      }
    }
  }, [dispatch, error]);

  return (
    <Tappable
      className="btn btn-long btn-primary m-r-xs"
      disabled={isValidating || isUpdating}
      onClick={handleClick}
    >
      {rescheduleWithComms
        ? 'Reschedule lectures and send notifications'
        : 'Reschedule lectures without sending notifications'}
    </Tappable>
  );
}

export default ConfirmRevisedScheduleButton;
